/**
 * Normalize piece data to ensure consistent field names across the application
 * This handles different field naming conventions from database vs local data
 */

import { supabase, STORAGE_BUCKETS } from '../lib/supabase.js';

/**
 * Helper function to convert storage path to public URL
 */
const convertToPublicUrl = (url, bucket = STORAGE_BUCKETS.PREVIEWS) => {
  if (!url) return null;
  
  // If it's a data URL, return as-is
  if (url.startsWith('data:')) return url;
  
  // If it's a placeholder, return as-is
  if (url.startsWith('/images/')) return url;
  
  // If it's a Supabase storage URL, extract the path and regenerate it
  if (url.includes('/storage/v1/object/public/')) {
    try {
      const match = url.match(/\/storage\/v1\/object\/public\/([^\/]+)\/(.*)/);
      if (match) {
        const [, urlBucket, path] = match;
        const targetBucket = urlBucket || bucket;
        const { data } = supabase.storage.from(targetBucket).getPublicUrl(path);
        return data?.publicUrl || url;
      }
    } catch (error) {
      console.error('Error extracting path from storage URL:', error);
    }
  }
  
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  
  // Convert storage path to public URL
  try {
    const { data } = supabase.storage.from(bucket).getPublicUrl(url);
    return data?.publicUrl || url;
  } catch (error) {
    console.error('Error converting to public URL:', error);
    return url;
  }
};

export const normalizePiece = (piece) => {
  if (!piece) {
    return null;
  }


  // Create a normalized piece object with consistent field names
  const normalized = {
    // Basic info
    id: piece.id,
    name: piece.name,
    description: piece.description,
    category: piece.category,
    brand: piece.brand,
    color: piece.color,
    rarity: piece.rarity,
    
    // Pricing and availability
    price: piece.price,
    quantity: piece.quantity,
    totalMade: piece.total_made || piece.totalMade,
    
    // Royalty information
    royalty_bps: piece.royalty_bps !== undefined ? piece.royalty_bps : null,
    
    // Listing status and marketplace info
    listing_status: piece.listing_status,
    listingStatus: piece.listing_status || piece.listingStatus,
    status: piece.listing_status || piece.status || (piece.mint_address ? 'listed' : 'draft'),
    listing_price: piece.listing_price || piece.listingPrice,
    listingPrice: piece.listing_price || piece.listingPrice,
    listed_at: piece.listed_at || piece.listedAt,
    listedAt: piece.listed_at || piece.listedAt,
    sold_at: piece.sold_at || piece.soldAt,
    soldAt: piece.sold_at || piece.soldAt,
    last_sale_price_lamports: piece.last_sale_price_lamports || piece.lastSalePriceLamports,
    lastSalePriceLamports: piece.last_sale_price_lamports || piece.lastSalePriceLamports,
    mint_address: piece.mint_address || piece.mintAddress,
    mintAddress: piece.mint_address || piece.mintAddress,
    minted_at: piece.minted_at || piece.mintedAt,
    mintedAt: piece.minted_at || piece.mintedAt,
    holder_wallet: piece.holder_wallet || piece.holderWallet,
    holderWallet: piece.holder_wallet || piece.holderWallet,
    
    // Tags
    tags: Array.isArray(piece.tags) ? piece.tags : [],
    
    // Social links (piece-specific, filled in during creation)
    social_links: piece.social_links || {},
    
  // Asset URLs - pass through database values as-is
  thumbnailUrl: piece.thumbnail_url || piece.thumbnailUrl || piece.thumbnail?.dataUrl || '/images/piece-placeholder.png',
  posterUrl: (() => {
    const posterSource = piece.poster_url || piece.posterUrl || piece.poster?.dataUrl;
    return posterSource ? convertToPublicUrl(posterSource) : null;
  })(),
  previewUrl: (() => {
    const previewSource = piece.preview_glb_url || piece.previewUrl || piece.preview?.dataUrl || 
      // Use thumbnail_url as 3D model if it's a .glb/.gltf file
      ((piece.thumbnail_url?.includes('.glb') || piece.thumbnail_url?.includes('.gltf')) ? piece.thumbnail_url : null);
    return previewSource ? convertToPublicUrl(previewSource) : null;
  })(),
  modelUrl: (() => {
    const modelSource = piece.model_url || piece.modelUrl || piece.original?.dataUrl;
    return modelSource ? convertToPublicUrl(modelSource) : null;
  })(),
    
    // Creator info - handle both nested creator objects and flattened creator fields
    // Transform database fields to UI-friendly fields
    creator: (() => {
      const creatorData = piece.creators || piece.creator || {
        handle: piece.creator_handle,
        wallet: piece.creator_wallet,
        is_verified: piece.creator_verified,
        verification_type: piece.creator_verification_type,
        profile_picture_url: piece.creator_profile_picture,
        total_pieces_created: piece.creator_total_pieces_created
      };
      // If it already has username, it's been transformed
      if (creatorData.username) return creatorData;
      // Otherwise, map database fields to UI fields
      return {
        ...creatorData,
        username: creatorData.handle || creatorData.username,
        name: creatorData.handle || creatorData.name,
        piecesCount: creatorData.total_pieces_created || creatorData.piecesCount || 0,
        verified: creatorData.is_verified !== undefined ? creatorData.is_verified : creatorData.verified,
        verificationType: creatorData.verification_type || creatorData.verificationType || 'none',
        profilePicture: creatorData.profile_picture_url || creatorData.profilePicture,
        // Keep original field names as well for compatibility
        handle: creatorData.handle,
        total_pieces_created: creatorData.total_pieces_created,
        is_verified: creatorData.is_verified,
        verification_type: creatorData.verification_type,
        profile_picture_url: creatorData.profile_picture_url
      };
    })(),
    originalCreator: (() => {
      const creatorData = piece.originalCreator || piece.creators || piece.creator || {
        handle: piece.creator_handle,
        wallet: piece.creator_wallet,
        is_verified: piece.creator_verified,
        verification_type: piece.creator_verification_type,
        profile_picture_url: piece.creator_profile_picture,
        total_pieces_created: piece.creator_total_pieces_created
      };
      // If it already has username, it's been transformed
      if (creatorData.username) return creatorData;
      // Otherwise, map database fields to UI fields
      return {
        ...creatorData,
        username: creatorData.handle || creatorData.username,
        name: creatorData.handle || creatorData.name,
        piecesCount: creatorData.total_pieces_created || creatorData.piecesCount || 0,
        verified: creatorData.is_verified !== undefined ? creatorData.is_verified : creatorData.verified,
        verificationType: creatorData.verification_type || creatorData.verificationType || 'none',
        profilePicture: creatorData.profile_picture_url || creatorData.profilePicture,
        // Keep original field names as well for compatibility
        handle: creatorData.handle,
        total_pieces_created: creatorData.total_pieces_created,
        is_verified: creatorData.is_verified,
        verification_type: creatorData.verification_type,
        profile_picture_url: creatorData.profile_picture_url
      };
    })(),
    creatorId: piece.creator_id || piece.creatorId,
    creator_wallet: piece.creator_wallet || piece.creatorWallet,
    creatorWallet: piece.creator_wallet || piece.creatorWallet,
    creators: (() => {
      const creatorData = piece.creators || {
        handle: piece.creator_handle,
        wallet: piece.creator_wallet,
        is_verified: piece.creator_verified,
        verification_type: piece.creator_verification_type,
        profile_picture_url: piece.creator_profile_picture,
        total_pieces_created: piece.creator_total_pieces_created
      };
      
      // If it already has username, it's been transformed
      if (creatorData.username) return creatorData;
      // Otherwise, map database fields to UI fields
      const normalized = {
        ...creatorData,
        username: creatorData.handle || creatorData.username,
        name: creatorData.handle || creatorData.name,
        piecesCount: creatorData.total_pieces_created || creatorData.piecesCount || 0,
        verified: creatorData.is_verified !== undefined ? creatorData.is_verified : creatorData.verified,
        verificationType: creatorData.verification_type || creatorData.verificationType || 'none',
        profilePicture: creatorData.profile_picture_url || creatorData.profilePicture,
        // Keep original field names as well for compatibility
        handle: creatorData.handle,
        total_pieces_created: creatorData.total_pieces_created,
        is_verified: creatorData.is_verified,
        verification_type: creatorData.verification_type,
        profile_picture_url: creatorData.profile_picture_url
      };
      
      return normalized;
    })(), // Keep creators object for purchase flow
    
    // Holder info - handle both nested holder objects and flattened holder fields
    // Transform database fields to UI-friendly fields
    holder: (() => {
      const holderData = piece.holder || {
        handle: piece.holder_handle,
        wallet: piece.holder_wallet,
        is_verified: piece.holder_verified,
        profile_picture_url: piece.holder_profile_picture,
        total_pieces_created: piece.holder_total_pieces_created
      };
      // If no holder data, return null
      if (!holderData.wallet && !holderData.handle) return null;
      // If it already has username, it's been transformed
      if (holderData.username) return holderData;
      // Otherwise, map database fields to UI fields
      return {
        ...holderData,
        username: holderData.handle || holderData.username,
        name: holderData.handle || holderData.name,
        piecesCount: holderData.total_pieces_created || holderData.piecesCount || 0,
        verified: holderData.is_verified !== undefined ? holderData.is_verified : holderData.verified,
        profilePicture: holderData.profile_picture_url || holderData.profilePicture,
        // Keep original field names as well for compatibility
        handle: holderData.handle,
        total_pieces_created: holderData.total_pieces_created,
        is_verified: holderData.is_verified,
        profile_picture_url: holderData.profile_picture_url
      };
    })(),
    holderUsername: piece.holderUsername || piece.holder?.handle || piece.holder_handle || 'Anonymous',
    holderAddress: piece.holderAddress || piece.holder?.wallet || piece.holder_wallet || '0x...0000',
    
    // File info
    fileName: piece.file_name || piece.fileName,
    fileSize: piece.file_size || piece.fileSize,
    fileType: piece.file_type || piece.fileType,
    
    // Timestamps
    createdAt: piece.created_at || piece.createdAt,
    updatedAt: piece.updated_at || piece.updatedAt,
    
    // Additional metadata
    assetPaths: piece.asset_paths || piece.assetPaths,
    isVerified: piece.is_verified || piece.isVerified || false,
    
    // Keep original piece data for reference
    _original: piece
  };

  // Check for 3D model availability (new bucket/key format or legacy URL format)
  const hasBucketKey = (normalized.previewBucket && normalized.previewKey) || (normalized.modelBucket && normalized.modelKey);
  const hasLegacyUrl = normalized.previewUrl && (normalized.previewUrl.includes('.glb') || normalized.previewUrl.includes('.gltf'));
  const hasLegacyModelUrl = normalized.modelUrl && (normalized.modelUrl.includes('.glb') || normalized.modelUrl.includes('.gltf'));
  
  if (hasBucketKey || hasLegacyUrl || hasLegacyModelUrl) {
    normalized.has3DModel = true;
    
    // Determine model type
    if (normalized.previewKey && normalized.previewKey.includes('.glb')) {
      normalized.modelType = 'glb';
    } else if (normalized.modelKey && normalized.modelKey.includes('.glb')) {
      normalized.modelType = 'glb';
    } else if (hasLegacyUrl && normalized.previewUrl.includes('.glb')) {
      normalized.modelType = 'glb';
    } else if (hasLegacyModelUrl && normalized.modelUrl.includes('.glb')) {
      normalized.modelType = 'glb';
    } else {
      normalized.modelType = 'gltf';
    }
    
    // Ensure we have a preview URL for legacy compatibility
    if (!normalized.previewUrl && hasLegacyModelUrl) {
      normalized.previewUrl = normalized.modelUrl;
    }
  } else {
    normalized.has3DModel = false;
    normalized.modelType = null;
  }

  // Ensure we have a thumbnail
  if (!normalized.thumbnailUrl) {
    normalized.thumbnailUrl = '/images/piece-placeholder.png';
  }

  // Ensure we have a poster image
  if (!normalized.posterUrl) {
    normalized.posterUrl = normalized.thumbnailUrl;
  }


  return normalized;
};

/**
 * Normalize an array of pieces
 */
export const normalizePieces = (pieces) => {
  if (!Array.isArray(pieces)) {
    console.warn('⚠️ normalizePieces received non-array:', typeof pieces);
    return [];
  }
  
  return pieces.map(normalizePiece).filter(Boolean);
};

/**
 * Get the best available image URL for a piece (thumbnail > poster > placeholder)
 */
export const getBestImageUrl = (piece) => {
  const normalized = normalizePiece(piece);
  return normalized?.thumbnailUrl || normalized?.posterUrl || '/images/piece-placeholder.png';
};

/**
 * Get the 3D model URL for a piece if available
 */
export const get3DModelUrl = (piece) => {
  const normalized = normalizePiece(piece);
  return normalized?.has3DModel ? normalized.previewUrl : null;
};

export default {
  normalizePiece,
  normalizePieces,
  getBestImageUrl,
  get3DModelUrl
};